import type { Metric } from 'web-vitals';

/**
 * Core Web Vitals reporting (equipment-performance-instrumentation R1).
 *
 * THIS FILE WAS DEAD CODE. `index.tsx` called `reportWebVitals()` with NO handler, and the first line
 * was `if (onPerfEntry && onPerfEntry instanceof Function)` — so every metric it gathered was
 * discarded. The instrumentation was present, wired, and useless. Activating it is the only change in
 * this spec with user-visible potential, and it is additive.
 *
 * UPGRADED TO `web-vitals@4` (was 2.1.4), which is why this is a rewrite rather than an edit:
 *
 *  - **INP replaces FID.** v2 predates INP, and INP is the metric that actually describes "the panel
 *    feels unresponsive" — the thing Rob is reporting. Shipping a metric set without it would have
 *    silently omitted the one number that matters for interaction latency, which R1 explicitly
 *    forbids ("do not silently ship a metric set that omits the one that matters").
 *
 *    `onFID` is still EXPORTED by v4.2.4 (verified against the installed package — it is deprecated
 *    here and removed in v5), so dropping it is OUR choice, not the library's. It is dropped
 *    deliberately: FID was deprecated in favour of INP, and reporting both would imply FID still
 *    means something and would put two competing responsiveness numbers on the same dashboard. The
 *    middleware's metric-name enum has no `FID` member, so this decision is enforced on both sides.
 *  - The API is now `onLCP`/`onCLS`/… rather than `getLCP`/`getCLS`/…, and the callback receives a
 *    `Metric` carrying its own `name`, so the handler no longer has to be told which metric it holds.
 *
 * `apps/twin-viewer-public` remains on 2.1.4 — a deliberate, recorded drift (Rob's decision 4): it is
 * not on the Equipment path, so upgrading it would be scope this spec has no reason to take.
 *
 * The dynamic import is kept from the original: vitals reporting must never sit on the critical path
 * of the app it is measuring.
 */
export type WebVitalsHandler = (metric: Metric) => void;

const reportWebVitals = (onPerfEntry?: WebVitalsHandler) => {
  if (!onPerfEntry || typeof onPerfEntry !== 'function') return;

  import('web-vitals')
    .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      onCLS(onPerfEntry);
      onFCP(onPerfEntry);
      // The replacement for FID, and the reason for the v4 upgrade.
      onINP(onPerfEntry);
      onLCP(onPerfEntry);
      onTTFB(onPerfEntry);
      // Swallow a failed chunk load (Copilot on #1145). Without this, a corrupted cache or a dropped
      // network on this lazy chunk becomes an unhandled promise rejection — noise in the console and in
      // any error reporter, caused by the measuring apparatus rather than by the app. The rule for
      // everything in this spec is that instrumentation must never break, or appear to break, the app it
      // measures; losing vitals for one session is the correct failure.
    })
    .catch(() => undefined);
};

export default reportWebVitals;
